import React from 'react';
import ReactDOM from 'react-dom';
import { FloatingBanner } from './floating-banner';
import { getUniqueAlertId } from './util';

let containerSelector = '.floating-banner-container';
let autoDismissTime = 0;
let logger;

/**
 * Set config for floating banner api.
 * @param {string} selector - The CSS selector of floating banner container.
 *   Default value is '.floating-banner-container'.
 * @param {number} dismissTime - The auto dismiss time (ms) for the floating banner.
 *   No auto-dismissal if the value is less than or equal to 0.
 *   Default value is 0.
 * @param {object} logInstrumentation - The logger object containing log functions
 *   Default value is undefined.
 * @return {void}
 */
const setConfig = ({
  selector,
  dismissTime,
  logInstrumentation,
}) => {
  containerSelector = selector || containerSelector;
  autoDismissTime = dismissTime || autoDismissTime;
  logger = logInstrumentation;
};

const newAlertId = () => undefined;

/**
 * Render the floating banner and auto dismiss it after autoDismissTime.
 * @param {object} alert - the alert plain object.
 * @param {string|HTMLString} [alert.message] - The alert message, which could be an HTML string.
 * @param {string} [alert.level='Confirmation'] - The level of alert, which could be
 *   'Error', 'Warning', 'Info' or 'Confirmation'.
 * @param {boolean} [alert.hasIcon=true] - If these is icon for the alert which is shown
 *   at left side of alert message.
 * @param {HTMLString} [alert.icon] - The icon template, default to be
 *   ```span.alert__icon```.
 * @param {boolean} [alert.dismissible=true] - If the alert is dismissible.
 * @param {object} [alert.action=null] - The alert action.
 * @param {object} [alert.action.message] - The alert action message, which is a plain string.
 * @param {object} [alert.action.callback] - The alert action callback, which will be called
 *   when action button clicked by user.
 * @return {undefined}
 */
const render = (alert) => {
  const containerSelectDom = document.querySelector(containerSelector);

  if (containerSelectDom) {
    ReactDOM.render(
      <FloatingBanner
        alert={alert}
        autoDismissTime={autoDismissTime}
        logInstrumentation={logger}
        key={getUniqueAlertId()}
      />,
      document.querySelector(containerSelector)
    );
  }

  return undefined;
};

const remove = () => render();

/**
 * Dismiss the floating banner.
 * @return {void}
 */
const dismissAll = () => {
  remove();
};

/**
 * Dismiss the floating banner.
 * @return {void}
 */
const dismiss = () => {
  remove();
};

/**
 * Show the floating banner.
 * @param {object} alert - the alert plain object.
 * @return {undefined}
 */
const show = alert => render(alert);

/**
 * Show error alert for the floating banner.
 * @param {string|HTMLString} message - The alert message, which could be an HTML string.
 * @return {undefined}
 */
const showError = message => show({
  message,
  level: 'Error',
});

/**
 * Show warning alert for the floating banner.
 * @param {string|HTMLString} message - The alert message, which could be an HTML string.
 * @return {undefined}
 */
const showWarning = message => show({
  message,
  level: 'Warning',
});

/**
 * Show info alert for the floating banner.
 * @param {string|HTMLString} message - The alert message, which could be an HTML string.
 * @return {undefined}
 */
const showInfo = message => show({
  message,
  level: 'Info',
});

/**
 * Show confirmation alert for the floating banner.
 * @param {string|HTMLString} message - The alert message, which could be an HTML string.
 * @return {undefined}
 */
const showConfirmation = message => show({
  message,
  level: 'Confirmation',
});

/**
 * Toast given message as an error.
 * @param {string|HTMLString} message - The alert message, which could be an HTML string.
 * @return {void}
 */
const toastError = (message) => {
  render({
    message,
    level: 'Error',
  });
};

/**
 * Toast given message as an warning.
 * @param {string|HTMLString} message - The alert message, which could be an HTML string.
 * @return {void}
 */
const toastWarning = (message) => {
  render({
    message,
    level: 'Warning',
  });
};

/**
 * Toast given message as an info.
 * @param {string|HTMLString} message - The alert message, which could be an HTML string.
 * @return {void}
 */
const toastInfo = (message) => {
  render({
    message,
    level: 'Info',
  });
};

/**
 * Toast given message as an confirmation.
 * @param {string|HTMLString} message - The alert message, which could be an HTML string.
 * @return {void}
 */
const toastConfirmation = (message) => {
  render({
    message,
    level: 'Confirmation',
  });
};

export {
  setConfig,
  newAlertId,
  dismissAll,
  show,
  showError,
  showWarning,
  showInfo,
  showConfirmation,
  dismiss,
  toastError,
  toastWarning,
  toastInfo,
  toastConfirmation,
};
